import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  View,
  Text,
  Platform,
  Dimensions,
  Image,
  Linking,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Modal from 'react-native-modal';
import * as Network from 'expo-network';

import Colors from '../../../constants/Colors';
import stringFunctions from '../../../utils/stringFunctions';
import tefap from '../../../utils/tefap';
import Layout from '../../../constants/Layout';
import Svg from '../../Svg';
import { wrapper } from '../../../utils/asyncFunctions';
import MyFloatingLabelInput from '../../form/MyFloatingLabelInput';
import PlusMinus from '../../form/PlusMinus';
import Selector from '../../form/Selector';
import AddressSelector from '../AddressSelector';
import forceComponentUpdate from '../../../utils/forceComponentUpdate';
import HTML from 'react-native-render-html';
import { TefapEligibility } from '../../../types';
import { TefapAttestationProps } from './TefapAttestationProps';
import { cloneDeep } from 'lodash';
import HouseholdSize from '../../form/HouseholdSize/HouseholdSize';

// Visible address fields differ by state
// Notes:
// - Colorado/New York TEFAP 2025 hides all fields except state and zip
const hiddenAddressFieldsByState = {
  CO: ['address_street_1', 'address_street_2', 'city'],
  NY: ['address_street_1', 'address_street_2', 'city'],
};
const hiddenAddressFieldsDefault = [];

/**
 * Tefap Attestation
 * @param {TefapAttestationProps} props
 */
function TefapAttestation(props) {
  const { isVisible, onCancel, translation, registration, actions, visit, pantry } = props;
  const { tefapConfig } = registration;

  const { capitalize } = stringFunctions;
  const labels = {
    zip_code: translation.app_personal_edittext_zipcode,
    zipcode: capitalize(translation.zipcode),
    tefap: translation.tefap_form,
    cancel: translation.app_button_cancel,
    ok: translation.app_button_ok,
    yes: translation.app_button_yes,
    no: translation.app_button_no,
    next: translation.app_button_next,
    back: translation.app_button_back,
    submit: translation.app_button_submit,
    close: translation.app_button_close,
    programs: {},
    signature: translation.tefap_sign_below,
    city: translation.city,
    address_street_1: translation.address_street_1,
    address_street_2: translation.address_street_2,
    usda_title: translation.tefap_form_usda_title,
    usda_subheader: translation.tefap_form_usda_subheader,
    usda_body: translation.tefap_form_usda_body,
  };

  // usda, summary, zip_code, benefits, family_size, income, eligible, ineligable, thanks
  const [currentFrame, setCurrentFrame] = useState('usda');
  const [hasTefapChanged, setHasTefapChanged] = useState(false);
  const [reason, setReason] = useState('income');
  const [signatureOk, setSignatureOk] = useState(false);
  const [scrollOffset, setScrollOffset] = useState(null);
  const scrollViewRef = useRef(null);
  const [rerenderFlag, setRerenderFlag] = useState(false);
  const [contentContainerLayout, setContentContainerLayout] = useState({ height: 0 });

  const hiddenAddressFields = hiddenAddressFieldsByState[pantry.state] ?? hiddenAddressFieldsDefault;

  const defaultValuesByState = {
    CO: {
      state: pantry.state,
    },
    NY: {
      state: pantry.state,
    },
  };

  // This is the same initialization logic as the Pantry App:
  // https://github.com/PlentifulApp/plentiful-pantry/blob/d5d3216f7a2dbc7c3be54398669af7e1357444c5/src/js/components/Schedule/TefapWrapper/TefapWrapper.js#L27-L48
  const [user, setUser] = useState(() => {
    let initialValues = cloneDeep(registration.user);

    // if defaults are defined for a specific state, apply them only if not already set
    // (as to not override the client-level data cloned above)
    if (defaultValuesByState[pantry.state]) {
      const defaults = defaultValuesByState[pantry.state];
      Object.keys(defaults).forEach((field) => {
        // be explicit because some set values are falsy
        if (
          initialValues[field] === undefined ||
          initialValues[field] === null ||
          initialValues[field] === ''
        ) {
          initialValues[field] = defaults[field];
        }
      });
    }

    return initialValues;
  });

  const handleForceUpdate = () => {
    forceComponentUpdate([rerenderFlag, setRerenderFlag]);
  };
  const insets = useSafeAreaInsets();

  const handleScrollTo = (p) => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo(p);
    }
  };

  const benefits = tefap.getStatePrograms(tefapConfig, user);
  labels.programs = Object.fromEntries(benefits.map((benefit) => [benefit.id, benefit.name]));

  const isBenefitRecipient = (user) => {
    return benefits.filter((program) => tefap.userParticipates(user, program.id)).length >= 1;
  };

  const isBelowMinIncome = (user) => {
    return Boolean(user.below_min_household_income);
  };

  /**
   * Are client and pantry quaranteed in different states?
   * @returns
   */
  const differentStates = (org, client) => {
    if (org && org.state && org.state !== '') {
      if (client && client.state && client.state !== '') {
        if (org.state !== client.state) {
          return true;
        }
      }
    }
    return false;
  };

  const goNext = () => {
    switch (currentFrame) {
      case 'usda':
        if (
          registration.user.tefap_attestation_complete &&
          visit &&
          visit.id &&
          visit.tefap_attestation == null
        ) {
          setCurrentFrame('summary');
          setHasTefapChanged(true);
        } else {
          setCurrentFrame('zip_code');
        }
        break;
      case 'summary':
        resetUserTEFAPInfo();
        setCurrentFrame('zip_code');
        break;
      case 'zip_code':
        if (differentStates(pantry, user)) {
          setCurrentFrame('ineligible');
        } else {
          setCurrentFrame('benefits');
        }
        break;
      case 'benefits':
        if (isBenefitRecipient(user)) {
          setCurrentFrame('eligible');
          setReason('participate');
        } else {
          setCurrentFrame('family_size');
          setReason('income');
        }
        break;
      case 'family_size':
        setCurrentFrame('income');
        break;
      case 'income':
        if (isBelowMinIncome(user)) {
          setCurrentFrame('eligible');
          setReason('income');
        } else {
          setCurrentFrame('ineligible');
        }
        break;
      case 'eligible':
        submitTefap();
        setCurrentFrame('thanks');
        break;
      case 'ineligible':
        if (user.tefap_attestation_complete && visit && visit.id && visit.tefap_attestation == null) {
          setCurrentFrame('summary');
        } else {
          setCurrentFrame('zip_code');
        }
        break;
      case 'thanks':
        onCancel();
        break;
      default:
        break;
    }
  };

  const goBack = () => {
    switch (currentFrame) {
      case 'usda':
        onCancel();
        break;
      case 'summary':
        setCurrentFrame('usda');
        break;
      case 'zip_code':
        if (hasTefapChanged) {
          setCurrentFrame('summary');
        } else {
          setCurrentFrame('usda');
        }
        break;
      case 'benefits':
        setCurrentFrame('zip_code');
        break;
      case 'family_size':
        setCurrentFrame('benefits');
        break;
      case 'income':
        setCurrentFrame('family_size');
        break;
      case 'eligible':
        if (reason == 'participate') {
          setCurrentFrame('benefits');
        } else {
          setCurrentFrame('income');
        }
        break;
      case 'ineligible':
        setCurrentFrame('income');
        break;
      default:
        break;
    }
  };

  const resetUserTEFAPInfo = () => {
    const resetPrograms = benefits.map((benefit) => ({
      public_assistance_program_id: benefit.id,
      participates: 0,
    }));

    const userWithResetTEFAPInfo = {
      ...user,
      tefap_expiration_date: null,
      tefap_attestation: 0,
      tefap_attestation_complete: 0,

      assistance_programs: resetPrograms,
      below_min_household_income: 0,
    };

    // use setState user with userWithResetTEFAPInfo
    setUser(Object.assign({}, userWithResetTEFAPInfo));
  };

  const retake = () => {
    setCurrentFrame('summary');
    setReason('income');
  };

  const setChanges = (field, value) => {
    const updatedUser = Object.assign({}, user);
    updatedUser[field] = value;
    setUser(updatedUser);
  };

  const setCheckboxChanges = (name, value) => {
    const field = name.toLowerCase();
    const updatedUser = Object.assign({}, user);
    if (value !== undefined) {
      updatedUser[field] = value;
    } else {
      updatedUser[field] === 1 ? (updatedUser[field] = 0) : (updatedUser[field] = 1);
    }
    setUser(updatedUser);
  };

  const isProgramParticipant = (program_id) => tefap.userParticipates(user, program_id);

  const toggleProgramParticipation = (program_id) => {
    const updatedUser = Object.assign({}, user);
    const program = updatedUser.assistance_programs.find(
      (program) => program.public_assistance_program_id === program_id
    );
    if (program !== undefined) {
      program.participates = program.participates === 1 ? 0 : 1;
    } else {
      updatedUser.assistance_programs.push({
        public_assistance_program_id: program_id,
        participates: 1,
      });
    }

    setUser(updatedUser);
  };

  const renderInputElement = (key, keyboardType) => {
    if (user.signature == undefined) user.signature = '';
    let value = user[key];
    let disabled = false;
    if (value || value == 0) value = value.toString();
    const label = labels[key];

    if (key.indexOf('family_members') !== -1) {
      return (
        <View style={styles.inputContainer}>
          <Text style={styles.checkBoxLabel}>{label}</Text>
          <PlusMinus
            value={Number(value)}
            onChange={(text) => {
              setChanges(key, text);
            }}
            min={0}
            max={8}
          />
        </View>
      );
    }

    if (currentFrame === 'zip_code') {
      return (
        <View style={styles.inputContainer} testID={`tefap-attestation-input-${key}-view`}>
          <MyFloatingLabelInput
            label={label}
            style={styles.inputField}
            onChangeText={(text) => {
              if (key === 'zip_code' && text) {
                // filter out non-digit characters
                text = text.replace(/\D/g, '');
              }
              setChanges(key, text);
            }}
            value={value}
            keyboardType={keyboardType}
            disabled={disabled}
            width="100%"
            isRequired={tefap.isRequiredTEFAPAddressField(pantry, key)}
            translation={translation}
            testID={`tefap-attestation-input-${key}`}
          />
        </View>
      );
    }

    return (
      <View style={styles.inputContainer}>
        <MyFloatingLabelInput
          label={label}
          style={styles.inputField}
          onChangeText={(text) => {
            setChanges(key, text);
          }}
          value={value}
          keyboardType={keyboardType}
          disabled={disabled}
          width="100%"
        />
      </View>
    );
  };

  const onAddressSelect = (addressDetails) => {
    const updatedUser = Object.assign({}, user);
    Object.keys(addressDetails).forEach((field) => {
      updatedUser[field] = addressDetails[field];
    });
    setUser(updatedUser);
  };

  const submitTefap = async () => {
    const updatedUser = Object.assign({}, user);
    updatedUser.below_min_household_income = isBelowMinIncome(updatedUser) ? 1 : 0;
    if (isBenefitRecipient(updatedUser) || updatedUser.below_min_household_income) {
      // if eligible, we need a signature

      if (!hasTefapChanged && !signatureOk) return;
      let date = new Date();
      const [error, ipAddress] = await wrapper(Network.getIpAddressAsync());
      if (error) console.log(error);

      updatedUser.is_eligible_for_tefap = 1;
      updatedUser.tefap_signature = user.signature;
      updatedUser.tefap_expiration_date = new Date(date.getFullYear() + 1, date.getMonth(), date.getDate());
      updatedUser.ip_address = ipAddress;
      // this `tefap_attestation` param is used by the API to update tefap eligibility status in the visit:
      // https://github.com/PlentifulApp/plentiful-api/blob/a975afe00987c24f43f3df7350222ca8995d958c/api/controllers/UserController.js#L796
      updatedUser.tefap_attestation = TefapEligibility.ATTESTED_AND_ELIGIBLE;
    } else {
      updatedUser.is_eligible_for_tefap = 0;
      updatedUser.tefap_signature = null;
      updatedUser.tefap_expiration_date = null;
      updatedUser.tefap_attestation = TefapEligibility.ATTESTED_AND_NOT_ELIGIBLE;
    }
    updatedUser.submitted_tefap = true;
    updatedUser.tefap_attestation_complete = 1;
    if (visit && visit.pantry_id) updatedUser.pantry_id = visit.pantry_id;
    if (visit && visit.id) updatedUser.visit_id = visit.id;

    actions.updateUser(updatedUser, () => {
      const isLoggedIn = true;
      actions.getVisits(() => {
        actions.showSinglePantry(props.organizationId, isLoggedIn);
        visit.tefap_attestation = 1;
        actions.updateVisit(visit);
      });
    });
    setCurrentFrame('thanks');
  };
  const deviceWidth = Dimensions.get('window').width - insets.left - insets.right;
  const deviceHeight = Dimensions.get('window').height - insets.top - insets.bottom;
  let income = tefap.getTefapIncomeLimit(user, tefapConfig);
  const tefapReason = tefap.getTefapReason(user, translation, tefapConfig, pantry);

  let modalHeader = (
    <View style={styles.header}>
      <Text style={styles.headerText}>{labels.tefap}</Text>
      <TouchableOpacity onPress={onCancel}>
        <Svg icon="close" color={Colors.white} width={24} height={24} />
      </TouchableOpacity>
    </View>
  );
  if (currentFrame == 'thanks') modalHeader = null;

  let mainContent = null;
  const goBackButton = (
    <TouchableOpacity
      onPress={goBack}
      style={[styles.formButton, styles.backButton]}
      testID="prev-tefap"
      key="prev-tefap"
    >
      <Text style={[styles.buttonText, styles.cancelText]}>{labels.back}</Text>
    </TouchableOpacity>
  );
  const goNextButton = (
    <TouchableOpacity
      onPress={goNext}
      style={[styles.formButton, styles.confirmButton]}
      testID="next-tefap"
      key="next-tefap"
    >
      <Text style={styles.buttonText}>{labels.next}</Text>
    </TouchableOpacity>
  );
  let buttonRow = [goBackButton, goNextButton];
  let tefapEligibleWithReason = translation.tefap_not_eligible;
  if (user.is_eligible_for_tefap && !tefapReason.out_of_state) {
    tefapEligibleWithReason = translation.tefap_eligible;
  }
  tefapEligibleWithReason += ` ${translation.because} ${tefapReason.reason}.`;
  if (!user.is_eligible_for_tefap) {
    tefapEligibleWithReason += ` ${translation.tefap_still_get_food}`;
  }

  const usdaLink = 'https://www.usda.gov/non-discrimination-statement';
  const openUSDA = () => {
    Linking.openURL(usdaLink);
  };

  const toggleSignatureOk = () => {
    setSignatureOk(!signatureOk);
  };

  const onContentContainerLayout = (event) => {
    const { height } = event.nativeEvent.layout;
    setContentContainerLayout({ height });
  };

  switch (currentFrame) {
    case 'usda':
      mainContent = (
        <View onLayout={onContentContainerLayout} style={styles.subheader}>
          <Text style={[styles.subHeaderText, styles.boldText, styles.textRow, styles.inlineHeader]}>
            {labels.usda_title}
          </Text>
          <Text style={[styles.subHeaderText, styles.boldText, styles.textRow]}>{labels.usda_subheader}</Text>
          <HTML style={[styles.subHeaderText, styles.textRow]} source={{ html: labels.usda_body }} />
        </View>
      );
      buttonRow = [
        goBackButton,
        <TouchableOpacity
          onPress={goNext}
          style={[styles.formButton, styles.confirmButton]}
          testID="next-tefap"
          key="next-tefap"
        >
          <Text style={styles.buttonText}>{labels.next}</Text>
        </TouchableOpacity>,
      ];
      break;
    case 'summary':
      mainContent = (
        <View onLayout={onContentContainerLayout}>
          <View style={styles.subheader}>
            <Text style={styles.subHeaderText}>{tefapEligibleWithReason}</Text>
            <Text style={styles.subHeaderText}>{translation.tefap_info_changed}</Text>
          </View>
        </View>
      );
      buttonRow = [
        <TouchableOpacity
          onPress={submitTefap}
          style={[styles.formButton, styles.confirmButton]}
          testID="no-submit-tefap"
          key="no-submit-tefap"
        >
          <Text style={styles.buttonText}>{translation.app_button_no}</Text>
        </TouchableOpacity>,
        <TouchableOpacity
          onPress={goNext}
          style={[styles.formButton, styles.confirmButton]}
          testID="yes-retake-tefap"
          key="yes-retake-tefap"
        >
          <Text style={styles.buttonText}>{translation.tefap_yes_retake}</Text>
        </TouchableOpacity>,
      ];
      break;
    case 'zip_code':
      mainContent = (
        <View onLayout={onContentContainerLayout}>
          <View style={styles.subheader}>
            <Text style={styles.subHeaderText}>{translation.tefap_address_correct}</Text>
          </View>

          <View style={[styles.infoCard, styles.addressContainer]}>
            {!hiddenAddressFields.includes('address_street_1') &&
              !hiddenAddressFields.includes('address_street_2') && (
                <View
                  style={[
                    styles.infoCardRow,
                    !hiddenAddressFields.includes('address_street_1') && styles.placesContainer,
                  ]}
                >
                  {!hiddenAddressFields.includes('address_street_1') && (
                    <View style={[styles.smallRow, styles.placesContainer]}>
                      <View style={styles.inputElement}>
                        <AddressSelector
                          translation={translation}
                          onSelect={onAddressSelect}
                          onChange={(value) => {
                            setChanges('address_street_1', value);
                          }}
                          addressStreet1={user.address_street_1}
                          state={user.state}
                          city={user.city}
                          handleForceUpdate={handleForceUpdate}
                          isRequired={tefap.isRequiredTEFAPAddressField(pantry, 'address_street_1')}
                          testID={'tefap-attestation-input-address_street_1'}
                        />
                      </View>
                    </View>
                  )}
                  {!hiddenAddressFields.includes('address_street_2') && (
                    <View style={styles.smallRow}>
                      <View style={styles.inputElement}>{renderInputElement('address_street_2')}</View>
                    </View>
                  )}
                </View>
              )}
            <View style={styles.infoCardRow}>
              <View style={[styles.smallRow, styles.mixedInputRow]}>
                {!hiddenAddressFields.includes('city') && (
                  <View style={styles.inputElement}>{renderInputElement('city')}</View>
                )}
                {!hiddenAddressFields.includes('state') && (
                  <View style={styles.inputElement}>
                    <Selector
                      onChange={setChanges}
                      label={translation.state}
                      field={'state'}
                      currentValue={user['state']}
                      dataset={'state'}
                      translation={translation}
                      isRequired={tefap.isRequiredTEFAPAddressField(pantry, 'state')}
                      testID={'tefap-attestation-input-state'}
                    />
                  </View>
                )}
              </View>
              <View style={styles.smallRow}>
                {!hiddenAddressFields.includes('zip_code') && (
                  <View
                    style={[styles.inputElement, styles.zipCodeContainer]}
                    testID="tefap-attestation-input-state-view"
                  >
                    {renderInputElement('zip_code', 'number-pad')}
                  </View>
                )}
              </View>
            </View>
          </View>
        </View>
      );

      buttonRow = [
        goBackButton,
        <TouchableOpacity
          onPress={tefap.addressComplete(pantry, user) ? goNext : null}
          style={[
            styles.formButton,
            tefap.addressComplete(pantry, user) ? styles.confirmButton : styles.disabledButton,
          ]}
          testID="next-tefap"
          key="next-tefap"
          disabled={!tefap.addressComplete(pantry, user)}
        >
          <Text style={styles.buttonText}>{labels.next}</Text>
        </TouchableOpacity>,
      ];

      break;
    case 'benefits':
      mainContent = (
        <View onLayout={onContentContainerLayout}>
          <View style={styles.benefitsCard}>
            <View style={styles.subheader}>
              <Text style={styles.subHeaderText}>{`${translation.tefap_participate_in_programs}\n`}</Text>
              <Text style={[styles.subHeaderText, styles.boldText]}>
                {translation.tefap_check_all_that_apply}
              </Text>
            </View>
            {benefits.map((benefit, index) => (
              <View key={index} style={styles.selector}>
                <View style={styles.checkboxRow} key={benefit.id}>
                  <TouchableOpacity
                    style={styles.checkbox}
                    onPress={() => toggleProgramParticipation(benefit.id)}
                    testID={`checkbox-benefit-${benefit.id}`}
                  >
                    <Svg
                      icon={isProgramParticipant(benefit.id) ? 'square_checked' : 'square_unchecked'}
                      style={styles.checkbox}
                    />
                  </TouchableOpacity>
                  <Text style={styles.checkBoxLabel}>{labels.programs[benefit.id]}</Text>
                </View>
              </View>
            ))}
          </View>
        </View>
      );
      break;
    case 'family_size':
      mainContent = (
        <View onLayout={onContentContainerLayout}>
          <View style={styles.subheader}>
            <Text style={styles.subHeaderText}>
              {translation.tefap_household_numbers_correct}
              {` `}
              {translation.tefap_household_dont_include_yourself}
            </Text>
          </View>
          <View style={styles.infoCard}>
            <View style={styles.infoCardRow}>
              <HouseholdSize
                familyMemberCounts={{
                  adultFamilyMembers: user['adult_family_members'] ?? 0,
                  childFamilyMembers: user['child_family_members'] ?? 0,
                  seniorFamilyMembers: user['senior_family_members'] ?? 0,
                }}
                state={pantry.state}
                language={registration.language}
                labelStyle={styles.checkBoxLabel}
                onChange={(key, value) => {
                  setChanges(key, value);
                }}
              ></HouseholdSize>
            </View>
          </View>
        </View>
      );
      break;
    case 'income':
      const { adult_family_members, child_family_members, senior_family_members } = user;

      const members =
        1 + Number(adult_family_members) + Number(child_family_members) + Number(senior_family_members);

      mainContent = (
        <View onLayout={onContentContainerLayout}>
          <View style={styles.subheader}>
            <Text style={styles.subHeaderText}>
              {translation.tefap_income_at_or_below_question} {income} {translation.tefap_for_household_size}{' '}
              {members === 0 ? 1 : members}?
            </Text>
          </View>
          <View style={styles.checkboxRowContainer}>
            <View style={styles.checkboxRow}>
              <TouchableOpacity
                style={styles.checkbox}
                onPress={() => setCheckboxChanges('below_min_household_income', 1)}
                testID={`checkbox-below-min-household-income-yes`}
              >
                <Svg
                  icon={user.below_min_household_income == 1 ? 'square_checked' : 'square_unchecked'}
                  style={styles.checkbox}
                />
              </TouchableOpacity>
              <Text style={styles.checkBoxLabel}>{translation.app_button_yes}</Text>
            </View>

            <View style={styles.horizontalSpacer} />

            <View style={styles.checkboxRow}>
              <TouchableOpacity
                style={styles.checkbox}
                onPress={() => setCheckboxChanges('below_min_household_income', 0)}
                testID={`checkbox-below-min-household-income-no`}
              >
                <Svg
                  icon={user.below_min_household_income == 0 ? 'square_checked' : 'square_unchecked'}
                  style={styles.checkbox}
                />
              </TouchableOpacity>
              <Text style={styles.checkBoxLabel}>{translation.app_button_no}</Text>
            </View>
          </View>
        </View>
      );
      const buttonEnabled = user.below_min_household_income == 0 || user.below_min_household_income == 1;
      buttonRow = [
        goBackButton,
        <TouchableOpacity
          onPress={buttonEnabled ? goNext : null}
          style={[styles.formButton, buttonEnabled ? styles.confirmButton : styles.disabledButton]}
          testID="next-tefap"
          key="next-tefap"
        >
          <Text style={styles.buttonText}>{labels.next}</Text>
        </TouchableOpacity>,
      ];
      break;
    case 'eligible':
      mainContent = (
        <View onLayout={onContentContainerLayout}>
          <View style={styles.subheader}>
            <View style={[styles.attentionBox, styles.attentionOk]}>
              <Text style={[styles.text, styles.boldText]}>
                {translation.tefap_eligible}
                <Text style={[styles.text, styles.regularText]}>
                  {` ${translation.because} ${tefapReason.reason}`}
                </Text>
              </Text>
            </View>
            <View style={styles.checkboxRow}>
              <TouchableOpacity
                style={styles.checkbox}
                onPress={toggleSignatureOk}
                testID={`checkbox-signature-ok`}
              >
                <Svg icon={signatureOk ? 'square_checked' : 'square_unchecked'} style={styles.checkbox} />
              </TouchableOpacity>
              <Text style={[styles.checkBoxLabel, styles.text, styles.textBottomPadding]}>
                {translation.tefap_form_checking_here}
              </Text>
            </View>
            <Text style={styles.text}>{translation.tefap_form_checking_1}</Text>
            <Text style={styles.text}>{translation[`tefap_form_checking_2_${user.state}`]}</Text>
            <Text style={styles.text}>{translation.tefap_form_checking_3}</Text>
            <Text style={styles.text}>{translation.tefap_form_checking_4}</Text>
            <Text style={styles.text}>{translation.tefap_form_checking_5_1}</Text>
            <Text style={[styles.text, styles.link]} onPress={openUSDA}>
              {translation.tefap_form_checking_5_2}
            </Text>
          </View>

          <View style={styles.signatureContainer}>
            <View style={styles.smallRow}>
              <View style={styles.inputElement}>{renderInputElement('signature')}</View>
            </View>
          </View>
        </View>
      );
      buttonRow = [
        goBackButton,
        <TouchableOpacity
          onPress={signatureOk ? submitTefap : null}
          style={[styles.formButton, signatureOk ? styles.confirmButton : styles.disabledButton]}
          testID="reset-tefap"
          key="reset-tefap"
        >
          <Text style={styles.buttonText}>{labels.submit}</Text>
        </TouchableOpacity>,
      ];
      break;
    case 'ineligible':
      mainContent = (
        <View onLayout={onContentContainerLayout}>
          <View style={styles.subheader}>
            <View style={[styles.attentionBox, styles.attentionNok]}>
              <Text style={[styles.text, styles.boldText]}>
                {translation.tefap_not_eligible}
                <Text style={[styles.text, styles.regularText]}>
                  {` ${translation.because} ${tefapReason.reason}. `}
                </Text>
                {translation.tefap_still_get_food}
              </Text>
            </View>
            <View style={styles.verticalSpace}>
              <Text style={[styles.text, styles.largeText]}>{translation.tefap_retake_attestation}</Text>
            </View>
          </View>
        </View>
      );
      buttonRow = [
        <TouchableOpacity
          onPress={submitTefap}
          style={[styles.formButton, styles.redButton]}
          testID="no-submit-tefap"
          key="no-submit-tefap"
        >
          <Text style={styles.buttonText}>{translation.tefap_no_submit}</Text>
        </TouchableOpacity>,
        <TouchableOpacity
          onPress={retake}
          style={[styles.formButton, styles.confirmButton]}
          testID="yes-retake-tefap"
          key="yes-retake-tefap"
        >
          <Text style={styles.buttonText}>{translation.tefap_yes_retake}</Text>
        </TouchableOpacity>,
      ];
      break;
    case 'thanks':
      mainContent = (
        <React.Fragment>
          <View style={[styles.subheader, styles.imageContainer]}>
            <Image
              source={require('../../../assets/images/plentiful_fruits.png')}
              style={styles.modalImage}
              resizeMode="contain"
            />
            <Text style={styles.introHeaderText}>{translation.tefap_thank_you}</Text>
            <Text style={styles.subHeaderText}>{translation.tefap_attestation_completed}</Text>
          </View>
        </React.Fragment>
      );
      buttonRow = [
        <TouchableOpacity
          onPress={onCancel}
          style={[styles.formButton, styles.backButton]}
          testID="no-submit-tefap"
          key="no-submit-tefap"
        >
          <Text style={[styles.buttonText, styles.cancelText]}>{translation.app_button_close}</Text>
        </TouchableOpacity>,
      ];
      break;
    default:
      break;
  }

  const buttonRowHeight = 72;
  const contentContainerHeight = contentContainerLayout.height + insets.top + insets.bottom + buttonRowHeight;

  return (
    <Modal
      isVisible={isVisible}
      deviceHeight={deviceHeight}
      deviceWidth={deviceWidth}
      style={{
        ...styles.modal,
        top: insets.top,
        left: insets.left,
        right: insets.right,
        bottom: insets.bottom,
      }}
      testID="tefap-form"
      onBackdropPress={onCancel}
      scrollTo={handleScrollTo}
      scrollOffset={scrollOffset}
      scrollOffsetMax={100}
      propagateSwipe={true}
    >
      <View style={styles.modalContent}>
        {modalHeader}
        <ScrollView
          style={styles.mainContent}
          contentContainerStyle={{
            ...styles.contentContainer,
            height: contentContainerHeight,
          }}
          keyboardShouldPersistTaps="handled"
          automaticallyAdjustKeyboardInsets={true}
        >
          {mainContent}

          <View style={styles.buttonRow}>{buttonRow}</View>

          {currentFrame == 4 ? <View style={styles.keyboardSpacer}></View> : null}
        </ScrollView>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  textRow: {
    marginTop: 20,
  },
  textCenter: {
    textAlign: 'center',
  },
  inlineHeader: {
    fontSize: 26,
  },
  modal: {
    alignItems: 'center',
    overflow: 'scroll',
    margin: Layout.isSmall ? 0 : 50,
  },
  modalContent: {
    width: Layout.fullWidth < 800 ? Layout.fullWidth : 720,
    height: Layout.isSmall ? Layout.fullHeight : 'inherit',
  },
  mainContent: {
    maxWidth: 720,
    height: Layout.isSmall ? Layout.fullHeight - 66 : 'inherit',
    width: '100%',
    backgroundColor: Colors.white,
  },
  contentContainer: {
    color: Colors.primaryColor,
    backgroundColor: Colors.lightBackground,
    paddingBottom: 10,
    position: 'relative',
  },
  noteContent: {
    padding: 16,
  },
  selector: {
    marginTop: 24,
    paddingHorizontal: 24,
    width: '100%',
  },
  checkboxRowContainer: {
    paddingLeft: 24,
    backgroundColor: Colors.white,
    flexDirection: 'row',
  },
  checkboxRow: {
    flexDirection: 'row',
  },
  checkBoxLabel: {
    paddingLeft: 0,
    fontSize: 16,
    fontFamily: 'roboto-regular',
  },
  benefitsCard: {
    padding: 16,
    borderBottomColor: Colors.white,
    borderBottomWidth: 0,
    backgroundColor: Colors.white,
  },
  smallRow: {
    paddingHorizontal: Layout.fullWidth < 732 ? 16 : 16, // 720 + 2 * 16
    width: Layout.isCompactScreen ? '100%' : '50%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  mixedInputRow: {
    alignItems: 'baseline',
  },
  subheader: {
    paddingHorizontal: 24, // 720 + 2 * 16
    paddingTop: 24,
    paddingBottom: 24,
    backgroundColor: Colors.white,
  },
  subHeaderText: {
    fontSize: 20,
    color: Colors.veryDarkGray,
  },
  attentionBox: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderRadius: 8,
    borderWidth: 2,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 24,
  },
  attentionOk: {
    borderColor: Colors.accentColor,
  },
  attentionNok: {
    borderColor: Colors.brightRed,
  },
  text: {
    fontSize: 18,
    color: Colors.veryDarkGray,
  },
  largeText: {
    fontSize: 20,
  },
  textBottomPadding: {
    paddingBottom: 12,
  },
  lightText: {
    color: Colors.mediumGray,
    marginTop: 12,
  },
  boldText: {
    fontWeight: 'bold',
  },
  regularText: {
    fontWeight: '400',
  },
  verticalSpace: {
    marginVertical: 24,
  },
  horizontalSpacer: {
    width: 42,
    height: 36,
  },
  inputElement: {
    flex: 1,
    paddingRight: 16,
    marginBottom: 16,
  },
  zipCodeContainer: {
    width: Layout.isCompactScreen ? '100%' : '60%',
  },
  header: {
    height: 66,
    maxWidth: 720,
    width: '100%',
    backgroundColor: Colors.primaryColor,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  headerText: {
    color: Colors.white,
    fontSize: 20,
    fontWeight: '500',
  },
  introHeaderText: {
    fontSize: 24,
  },
  body: {
    height: Platform.OS === 'web' ? 'auto' : 200,
    width: '100%',
    flexDirection: 'row',
    backgroundColor: Colors.white,
  },
  dropdown: {
    height: 50,
    width: 100,
    borderColor: Colors.white,
  },
  buttonRow: {
    height: 72,
    width: '100%',
    backgroundColor: Colors.lightGrayBackground,
    borderTopWidth: 1,
    borderTopColor: Colors.grayBorder,
    flexDirection: 'row',
    justifyContent: 'center',
    paddingTop: 18,
    paddingHorizontal: 11,
  },
  formButton: {
    borderRadius: 4,
    marginHorizontal: 13,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: Layout.isCompactScreen ? '100%' : '50%',
  },
  disabledButton: {
    backgroundColor: Colors.grayButton,
  },
  confirmButton: {
    backgroundColor: Colors.primaryColor,
  },
  backButton: {
    borderColor: Colors.primaryColor,
    borderWidth: 1,
  },
  redButton: {
    backgroundColor: Colors.brightRed,
  },
  buttonText: {
    color: Colors.white,
    textTransform: 'uppercase',
    fontWeight: '700',
  },
  cancelText: {
    color: Colors.primaryColor,
  },
  spinnerModal: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    zIndex: 1,
  },
  imageContainer: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    padding: 20,
  },
  modalImage: {
    width: 95,
    height: 68,
    marginBottom: 24,
    marginTop: 24,
    marginRight: 'auto',
    marginLeft: 'auto',
  },
  keyboardSpacer: {
    height: Platform.OS == 'web' ? 0 : 400,
  },
  link: {
    color: Colors.primaryColor,
  },
  infoCard: {
    padding: 16,
    paddingTop: 16,
    borderBottomColor: Colors.grayBorder,
    borderBottomWidth: 1,
    backgroundColor: Colors.white,
  },
  infoCardRow: {
    flexDirection: Layout.isCompactScreen ? 'column' : 'row',
    alignItems: Layout.isCompactScreen ? 'flex-start' : 'center',
    justifyContent: 'space-between',
  },
  checkboxCardRow: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  dobRow: {
    marginTop: Layout.isCompactScreen ? 0 : -26,
  },
  checkbox: {
    width: 32,
  },
  sectionHeaderText: {
    fontSize: 16,
    color: Colors.black,
    fontWeight: '600',
    paddingBottom: 12,
    paddingTop: 18,
  },
  addressContainer: {
    marginTop: 12,
    padding: 8,
  },
  placesContainer: {
    // This allows the Google Places suggestions to sit above the other form elements
    zIndex: 1,
    minHeight: 60,
  },
  signatureContainer: {
    backgroundColor: Colors.white,
    marginLeft: 8,
  },
});

TefapAttestation.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  translation: PropTypes.objectOf(PropTypes.any).isRequired,
  registration: PropTypes.objectOf(PropTypes.any).isRequired,
  actions: PropTypes.objectOf(PropTypes.any).isRequired,
  visit: PropTypes.objectOf(PropTypes.any),
  pantry: PropTypes.objectOf(PropTypes.any),
  organizationId: PropTypes.number.isRequired,
};

export default TefapAttestation;
